import { useEffect, useState, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";

// Lazy-load below-the-fold sections to reduce initial JS bundle
const ProcessSection = lazy(() => import("@/components/ProcessSection"));
const LeadershipSection = lazy(() => import("@/components/LeadershipSection"));
const TeamSection = lazy(() => import("@/components/TeamSection"));
const BenefitsSection = lazy(() => import("@/components/BenefitsSection"));
const InsightsSection = lazy(() => import("@/components/InsightsSection"));
const CaseStudiesSection = lazy(() => import("@/components/CaseStudiesSection"));
const ClutchReviewsSection = lazy(() => import("@/components/ClutchReviewsSection"));

interface SectionVisibility {
  hero: boolean;
  intro: boolean;
  process: boolean;
  leadership: boolean;
  team: boolean;
  benefits: boolean;
  caseStudies: boolean;
  insights: boolean;
  clutchBadge: boolean;
  clutchReviews: boolean;
}

const DEFAULT_VISIBILITY: SectionVisibility = {
  hero: true,
  intro: true,
  process: true,
  leadership: true,
  team: true,
  benefits: true,
  caseStudies: true,
  insights: true,
  clutchBadge: false,
  clutchReviews: false,
};

const Index = () => {
  const [visibility, setVisibility] = useState<SectionVisibility>(DEFAULT_VISIBILITY);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await (supabase as any)
        .from("public_site_settings")
        .select("section_visibility")
        .limit(1);
      if (data && data.length > 0) {
        const sv = data[0].section_visibility as Record<string, boolean>;
        setVisibility({
          hero: sv.hero ?? DEFAULT_VISIBILITY.hero,
          intro: sv.intro ?? DEFAULT_VISIBILITY.intro,
          process: sv.process ?? DEFAULT_VISIBILITY.process,
          leadership: sv.leadership ?? DEFAULT_VISIBILITY.leadership,
          team: sv.team ?? DEFAULT_VISIBILITY.team,
          benefits: sv.benefits ?? DEFAULT_VISIBILITY.benefits,
          caseStudies: sv.caseStudies ?? DEFAULT_VISIBILITY.caseStudies,
          insights: sv.insights ?? DEFAULT_VISIBILITY.insights,
          clutchBadge: sv.clutchBadge ?? DEFAULT_VISIBILITY.clutchBadge,
          clutchReviews: sv.clutchReviews ?? DEFAULT_VISIBILITY.clutchReviews,
        });
      }
    };
    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>TechNeed | Software Consulting</title>
        <meta
          name="description"
          content="TechNeed helps businesses build better software with expert consulting across data, AI, cloud, and product strategy."
        />
      </Helmet>
      <Navbar />
      {visibility.hero && <HeroSection />}
      <Suspense fallback={<div className="w-full py-24 space-y-16"><div className="container mx-auto px-6 space-y-6">{[...Array(3)].map((_, i) => (<div key={i} className="animate-pulse space-y-4"><div className="h-8 bg-muted/40 rounded w-1/3 mx-auto" /><div className="h-4 bg-muted/30 rounded w-2/3 mx-auto" /><div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">{[...Array(3)].map((_, j) => (<div key={j} className="h-48 bg-muted/20 rounded-xl" />))}</div></div>))}</div></div>}>
        {visibility.process && <ProcessSection />}
        {visibility.leadership && <LeadershipSection />}
        {visibility.team && <TeamSection />}
        {visibility.benefits && <BenefitsSection />}
        {visibility.caseStudies && <CaseStudiesSection useDatabase={visibility.clutchBadge} />}
        {visibility.clutchReviews && <ClutchReviewsSection />}
        {visibility.insights && <InsightsSection />}
      </Suspense>
      <Footer />
    </div>
  );
};

export default Index;
